import moment from "moment";
import React, { useCallback, useRef, useState } from "react";
import { OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import ReactDiffViewer from "react-diff-viewer";
import styled, { css, withTheme } from "styled-components";
import {
  AuditTrailMaxBytesLargeDiff,
  AuditTrailObjectIdRegex,
  AUDIT_TRAIL_EVENTS_PAGE_SIZE,
  AUDIT_TRAIL_PARAMS_NAMES,
} from "../../../modules/constants";
import { getTextBytes, updateURLQueryParams } from "../../../modules/utils";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import CustomDropdownLabels from "../../ui-library/CustomDropdown/CustomDropdownLabels";
import AuditTrailOutlineButton from "../../ui-library/Buttons/AuditTrailOutlineButton/AuditTrailOutlineButton";
import ActorFilterForm from "./ActorFilterForm/ActorFilterForm";
import DateTimeFilterForm from "./DateTimeFilterForm/DateTimeFilterForm";
import ResourceFilterForm from "./ResourceFilterForm/ResourceFilterForm";
import SubjectAccountFilterForm from "./SubjectAccountFilterForm/SubjectAccountFilterForm";

const StyledAuditTrailEventsTable = styled.div`
  thead {
    position: sticky;
    top: 80;
  }

  tbody {
    border-top: 2px solid #dee2e6 !important;

    .failed-entry {
      td {
        color: red !important;
      }
    }
  }
`;

const StrippedTr = styled.tr`
  ${(props) =>
    props.stripped &&
    css`
      --bs-table-accent-bg: var(--bs-table-striped-bg);
    `}
`;

const ThColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ThRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`;

const TdCloseFullDetails = styled.td`
  vertical-align: middle;
  text-align: center;
  border: 2px solid ${(props) => (props.open ? props.theme?.gray : "inherit")} !important;
  border-bottom: 2px solid ${(props) => (props.open ? "#dee2e6" : "inherit")} !important;

  @-moz-document url-prefix() {
    ${(props) =>
      props.open &&
      css`
        box-shadow: inset 0 2px 0px 0px ${(props) => props.theme?.gray} !important;
        border-top-width: 0px !important;

        ${(props) =>
          props.stripped &&
          css`
            background-color: var(--bs-table-accent-bg) !important;
          `}
      `}
  }
`;

const DiffViewContainer = styled.td`
  -webkit-animation: fadein 0.5s;
  -moz-animation: fadein 0.5s;
  -ms-animation: fadein 0.5s;
  -o-animation: fadein 0.5s;
  animation: fadein 0.5s;

  @media print {
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -ms-animation: none !important;
    -o-animation: none !important;
    animation: none !important;
  }

  padding: 0 !important;
  border: 2px solid ${(props) => props.theme?.gray} !important;
  letter-spacing: -1px;

  tbody {
    border-top-width: 0 !important;
  }
`;

const StyledTime = styled.div`
  font-family: monospace;
  letter-spacing: -1px;
`;
const StyledValue = styled.span`
  font-family: monospace;
  letter-spacing: -1px;
  white-space: pre-wrap;

  ${(props) =>
    props.diff &&
    !props.$hasMoreData &&
    css`
      cursor: zoom-in;
    `};

  ${(props) =>
    props.$hasMoreData &&
    css`
      max-height: 110px;
      overflow-y: hidden;
    `};
`;
const StyledMeta = styled.span`
  white-space: pre-wrap;
`;
const ResourceButton = styled.button`
  border: none;
  outline: none;
  background: transparent;

  span {
    font-family: monospace;
    letter-spacing: -1px;
    text-align: left;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledButton = styled.button`
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  color: ${(props) => (props.isError ? "red" : "inherit")};
  p {
    text-align: left;
    &:hover {
      text-decoration: underline;
    }
  }
  .email {
    word-break: break-word;
  }
  &.colorized {
    color: inherit;
    font-weight: inherit;
  }
  &.disabled {
    cursor: text;
    color: ${(props) => (props.isError ? "red" : "#212529")};
    p {
      &:hover {
        text-decoration: none;
      }
    }
  }
  .font-weight-bold {
    font-weight: 700;
  }
`;

const StyledTooltip = styled(Tooltip)`
  @media print {
    display: none;
  }
  .tooltip-inner {
    background-color: #a7208c;
    color: #fff;
    border-radius: 0.4rem;
  }
`;

const StyledTable = styled(Table)`
  &.table-hover tbody {
    tr:hover {
      background-color: ${(props) => props.theme?.lightBlue};
      --bs-table-hover-bg: rgba(0, 0, 0, 0);
    }
  }

  .actor-th {
    width: 120px;
    min-width: 120px;
  }

  @media print {
    font-size: 9pt;

    .actor-th {
      width: 85px;
      min-width: 85px;
    }
  }
`;

const StyledPrintEndOfRecords = styled.div`
  visibility: hidden;
  @media print {
    visibility: ${(props) => (props.hasMoreData ? "visible" : "hidden")};
  }
`;

const ColumnTitle = styled.span`
  cursor: pointer;

  ${(props) =>
    props.$filterApplied &&
    css`
      color: ${(props) => props.theme?.violetEggplant};
    `};
`;

const PrintableText = styled.span`
  display: none;
  @media print {
    display: inline-block;
  }
`;

const StyledAuditTrailOutlineButton = styled(AuditTrailOutlineButton)`
  width: 130px;
  max-width: 130px;
  min-width: 130px;
`;

export const AuditTrailEventsTable = (props) => {
  const {
    events,
    fetchAuditEvents,
    fetchAuditEventsByFilters,
    hasMoreData,
    setHasMoreData,
    user_sites,
    actions = [],
    actorTypes = [],
    statuses = [],
    sites,
    onChangeActorType,
    onChangeAction,
    onChangeStatus,
    onChangeSite,
    applyActorFilter,
    applySubjectAccountFilter,
    applyResourceFilter,
    onChangeBeforeAfterDateTimes,
    filterTags,
    updateEvents,
  } = props;

  const [dateTimeNow, setDateTimeNow] = useState(moment());

  const [filterActorFormTarget, setFilterActorFormTarget] = useState(null);
  const [filterSubjectAccountFormTarget, setFilterSubjectAccountFormTarget] =
    useState(null);
  const [filterResourceFormTarget, setFilterResourceFormTarget] =
    useState(null);
  const [filterDateTimesFormTarget, setFilterDateTimesFormTarget] =
    useState(null);

  const dropdownToggleDateTimeRef = useRef(null);
  const dropdownToggleActorRef = useRef(null);
  const dropdownToggleSubjectAccountRef = useRef(null);
  const dropdownToggleResourceRef = useRef(null);

  const dropdownToggleActorTypeRef = useRef(null);
  const dropdownToggleSubjectSiteRef = useRef(null);
  const dropdownToggleActionRef = useRef(null);
  const dropdownToggleStatusRef = useRef(null);

  const filtersInitialState = {
    filterAfter: null,
    filterBefore: null,
    filterActor: null,
    filterActorType: null,
    filterSubjectAccount: null,
    filterAction: null,
    filterStatus: null,
    filterSubjectSite: null,
    filterResource: null,
  };

  const handleStatus = (lastEvent) => {
    setHasMoreData("loading");
    return fetchAuditEvents(lastEvent).then((r) => {
      //If we have less than 200 returned results there is nothing more to load
      setHasMoreData(r < AUDIT_TRAIL_EVENTS_PAGE_SIZE ? false : true);
    });
  };

  const shrinkResource = (resource, charLength) => {
    if (resource === null) {
      return null;
    }

    if (AuditTrailObjectIdRegex.test(resource)) {
      const resourceSplitted = resource.split(" ");

      return (
        <>
          {resourceSplitted[0]}
          <br />
          {resourceSplitted[1].substring(0, charLength)}&#8230;
        </>
      );
    }

    return <>{resource.substring(0, charLength)}&#8230;</>;
  };

  const getFilters = useCallback(
    (key, filter, filters = filtersInitialState) => {
      switch (key) {
        case AUDIT_TRAIL_PARAMS_NAMES.actor:
          return {
            ...filters,
            filterActor: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.actorType:
          return {
            ...filters,
            filterActorType: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.subjectAccount:
          return {
            ...filters,
            filterSubjectAccount: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.action:
          return {
            ...filters,
            filterAction: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.status:
          return {
            ...filters,
            filterStatus: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.subjectSite:
          return {
            ...filters,
            filterSubjectSite: filter,
          };
        case AUDIT_TRAIL_PARAMS_NAMES.resource:
          return {
            ...filters,
            filterResource: filter,
          };
        default:
          return filtersInitialState;
      }
    },
    []
  );

  const onSelectResource = (key, filter) => {
    updateURLQueryParams(key, filter, "/audittrail", true);
    fetchAuditEventsByFilters(getFilters(key, filter))
      .then((r) => {
        //If we have less than 200 returned results there is nothing more to load
        setHasMoreData(r < AUDIT_TRAIL_EVENTS_PAGE_SIZE ? false : true);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const formatActionField = (string) => {
    const [category, subCategory, action] = string.split(":");
    return (
      <>
        {category}:<br />
        <b>
          {subCategory}
          {action && ":"}
        </b>
        {action && (
          <>
            <br />
            <b>{action}</b>
          </>
        )}
      </>
    );
  };

  const getFormattedTimeZoneOffset = (eventTimeZoneOffset) => {
    const timeZoneOffset = Math.ceil(eventTimeZoneOffset / 60);
    let timeZoneOffsetText = `${timeZoneOffset}h`;
    if (timeZoneOffset >= 0) {
      timeZoneOffsetText = `+${timeZoneOffsetText}`;
    }

    return timeZoneOffsetText;
  };

  const onSubmitActorFilterForm = (filterActor) => {
    applyActorFilter(filterActor);
    setFilterActorFormTarget(null);
  };

  const onSubmitSubjectAccountFilterForm = (filterSubjectAccount) => {
    applySubjectAccountFilter(filterSubjectAccount);
    setFilterSubjectAccountFormTarget(null);
  };

  const onSubmitResourceFilterForm = (filterResource) => {
    applyResourceFilter(filterResource);
    setFilterResourceFormTarget(null);
  };

  const onSubmitDateTimeFilterForm = (filterBefore, filterAfter) => {
    onChangeBeforeAfterDateTimes(filterBefore, filterAfter);
    setFilterDateTimesFormTarget(null);
  };

  const toggleMoreEventData = (event) => {
    const updatedEvents = events.map((e) => {
      if (e.id === event.id) {
        e.open = !event.open;
      }

      return e;
    });
    updateEvents(updatedEvents);
  };

  return (
    <>
      <ActorFilterForm
        show={!!filterActorFormTarget}
        onClose={() => setFilterActorFormTarget(null)}
        currentFilterActor={filterTags.filterActor?.value}
        onSubmit={onSubmitActorFilterForm}
        target={filterActorFormTarget}
      />

      <SubjectAccountFilterForm
        show={!!filterSubjectAccountFormTarget}
        onClose={() => setFilterSubjectAccountFormTarget(null)}
        currentFilterSubjectAccount={filterTags.filterSubjectAccount?.value}
        onSubmit={onSubmitSubjectAccountFilterForm}
        target={filterSubjectAccountFormTarget}
      />

      <ResourceFilterForm
        show={!!filterResourceFormTarget}
        onClose={() => setFilterResourceFormTarget(null)}
        currentFilterResource={filterTags.filterResource?.value}
        onSubmit={onSubmitResourceFilterForm}
        target={filterResourceFormTarget}
      />

      <DateTimeFilterForm
        show={!!filterDateTimesFormTarget}
        onClose={() => setFilterDateTimesFormTarget(null)}
        currentFilterAfter={
          filterTags.filterAfter?.value
            ? new Date(filterTags.filterAfter.value)
            : null
        }
        currentFilterBefore={
          filterTags.filterBefore?.value
            ? new Date(filterTags.filterBefore.value)
            : null
        }
        onSubmit={onSubmitDateTimeFilterForm}
        target={filterDateTimesFormTarget}
      />

      {events && events?.length > 0 ? (
        <div>
          <StyledAuditTrailEventsTable data-testid="at-evt-tbl">
            <StyledTable responsive bordered hover size="sm">
              <thead>
                <tr>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-date-time-title"
                        onClick={(e) =>
                          setFilterDateTimesFormTarget(
                            dropdownToggleDateTimeRef.current
                          )
                        }
                        $filterApplied={
                          filterTags?.filterAfter || filterTags?.filterBefore
                        }
                      >
                        Date/Time (UTC)
                      </ColumnTitle>
                      <CustomDropdownLabels
                        className="noprint"
                        onClick={(e) =>
                          setFilterDateTimesFormTarget(
                            dropdownToggleDateTimeRef.current
                          )
                        }
                        data-testid="at-evt-date-time"
                        size="small"
                        active={!!filterDateTimesFormTarget}
                        dropdownToggleRef={dropdownToggleDateTimeRef}
                      />
                    </ThRow>
                  </th>
                  <th className="actor-th">
                    <ThColumn>
                      <ThRow>
                        <ColumnTitle
                          data-testid="at-evt-actor-title"
                          onClick={(e) =>
                            setFilterActorFormTarget(
                              dropdownToggleActorRef.current
                            )
                          }
                          $filterApplied={filterTags?.filterActor}
                        >
                          Actor
                        </ColumnTitle>
                        <CustomDropdownLabels
                          className="noprint"
                          onClick={(e) =>
                            setFilterActorFormTarget(
                              dropdownToggleActorRef.current
                            )
                          }
                          data-testid="at-evt-actor"
                          size="small"
                          active={!!filterActorFormTarget}
                          dropdownToggleRef={dropdownToggleActorRef}
                        />
                      </ThRow>
                      <ThRow>
                        <ColumnTitle
                          data-testid="at-evt-actor-types-title"
                          onClick={() => {
                            dropdownToggleActorTypeRef.current?.click();
                            dropdownToggleActorTypeRef.current?.focus();
                          }}
                          $filterApplied={filterTags?.filterActorType}
                        >
                          Actor Type
                        </ColumnTitle>
                        <CustomDropdownLabels
                          className="noprint"
                          values={actorTypes}
                          limitheight={true}
                          onChange={(e) => onChangeActorType(e.value)}
                          data-testid="at-evt-actor-types"
                          size="small"
                          dropdownToggleRef={dropdownToggleActorTypeRef}
                        />
                      </ThRow>
                    </ThColumn>
                  </th>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-subject-account-title"
                        onClick={(e) =>
                          setFilterSubjectAccountFormTarget(
                            dropdownToggleSubjectAccountRef.current
                          )
                        }
                        $filterApplied={filterTags?.filterSubjectAccount}
                      >
                        Subject Account
                      </ColumnTitle>
                      <CustomDropdownLabels
                        className="noprint"
                        onClick={(e) =>
                          setFilterSubjectAccountFormTarget(
                            dropdownToggleSubjectAccountRef.current
                          )
                        }
                        data-testid="at-evt-subject-account"
                        size="small"
                        active={!!filterSubjectAccountFormTarget}
                        dropdownToggleRef={dropdownToggleSubjectAccountRef}
                      />
                    </ThRow>
                  </th>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-sites-title"
                        onClick={() => {
                          dropdownToggleSubjectSiteRef.current?.click();
                          dropdownToggleSubjectSiteRef.current?.focus();
                        }}
                        $filterApplied={filterTags?.filterSubjectSite}
                      >
                        Subject Site
                      </ColumnTitle>
                      {user_sites?.length === 0 && (
                        <CustomDropdownLabels
                          className="noprint"
                          values={sites}
                          width={100}
                          limitheight={true}
                          onChange={(e) => onChangeSite(e.value)}
                          data-testid="at-evt-sites"
                          size="small"
                          dropdownToggleRef={dropdownToggleSubjectSiteRef}
                        />
                      )}
                    </ThRow>
                  </th>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-actions-title"
                        onClick={() => {
                          dropdownToggleActionRef.current?.click();
                          dropdownToggleActionRef.current?.focus();
                        }}
                        $filterApplied={filterTags?.filterAction}
                      >
                        Action
                      </ColumnTitle>
                      <CustomDropdownLabels
                        className="noprint"
                        values={actions}
                        limitheight={true}
                        onChange={(e) => onChangeAction(e.value)}
                        data-testid="at-evt-actions"
                        size="small"
                        dropdownToggleRef={dropdownToggleActionRef}
                      />
                    </ThRow>
                  </th>
                  <th>Reason</th>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-statuses-title"
                        onClick={() => {
                          dropdownToggleStatusRef.current?.click();
                          dropdownToggleStatusRef.current?.focus();
                        }}
                        $filterApplied={filterTags?.filterStatus}
                      >
                        Status
                      </ColumnTitle>
                      <CustomDropdownLabels
                        className="noprint"
                        values={statuses}
                        limitheight={true}
                        onChange={(e) => onChangeStatus(e.value)}
                        data-testid="at-evt-statuses"
                        size="small"
                        dropdownToggleRef={dropdownToggleStatusRef}
                      />
                    </ThRow>
                  </th>
                  <th>
                    <ThRow>
                      <ColumnTitle
                        data-testid="at-evt-resource-title"
                        onClick={(e) =>
                          setFilterResourceFormTarget(
                            dropdownToggleResourceRef.current
                          )
                        }
                        $filterApplied={filterTags?.filterResource}
                      >
                        Object ID
                      </ColumnTitle>
                      <CustomDropdownLabels
                        className="noprint"
                        onClick={(e) =>
                          setFilterResourceFormTarget(
                            dropdownToggleResourceRef.current
                          )
                        }
                        data-testid="at-evt-object-id"
                        size="small"
                        active={!!filterResourceFormTarget}
                        dropdownToggleRef={dropdownToggleResourceRef}
                      />
                    </ThRow>
                  </th>
                  <th>Original Value</th>
                  <th>New Value</th>

                  <th>Extra Information</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, index) => {
                  const hasMoreDataOldValue =
                    getTextBytes(event.oldValue) > AuditTrailMaxBytesLargeDiff;
                  const hasMoreDataNewValue =
                    getTextBytes(event.newValue) > AuditTrailMaxBytesLargeDiff;
                  const evenRow = index % 2 === 0;
                  const hasMoreDataValues =
                    (event.newValue || event.oldValue) &&
                    (hasMoreDataOldValue || hasMoreDataNewValue);

                  const eventHappenedAt = moment
                    .utc(event.happenedAt)
                    .utcOffset(Number(event.timeZoneOffset));

                  return (
                    <React.Fragment key={`event-table-entry-${index}`}>
                      <StrippedTr
                        stripped={evenRow}
                        style={
                          event.status.includes("Failed:")
                            ? {
                                color: "red",
                              }
                            : {}
                        }
                        className={
                          event.status.includes("Failed:") ? "failed-entry" : ""
                        }
                      >
                        <td
                          rowSpan={hasMoreDataValues ? (event.open ? 3 : 1) : 1}
                        >
                          <OverlayTrigger
                            placement="top"
                            onEntering={() => {
                              setDateTimeNow(moment());
                            }}
                            overlay={
                              <StyledTooltip>
                                {eventHappenedAt.format("DD MMM YYYY")}
                                <br />
                                {eventHappenedAt.format("HH:mm:ss.SS")} (
                                {getFormattedTimeZoneOffset(
                                  event.timeZoneOffset
                                )}
                                )
                                <br />
                                {moment
                                  .duration(
                                    eventHappenedAt.diff(
                                      dateTimeNow
                                        .utc()
                                        .utcOffset(Number(event.timeZoneOffset))
                                    )
                                  )
                                  .humanize(true)}
                              </StyledTooltip>
                            }
                          >
                            <StyledTime>
                              {moment(event.happenedAt).format("DD MMM YYYY")}
                              <br />
                              {moment(event.happenedAt).format("HH:mm:ss.SS")}
                            </StyledTime>
                          </OverlayTrigger>
                        </td>
                        <td>
                          <StyledButton
                            isError={event.status.includes("Failed:")}
                            type="button"
                            data-testid={`at-evt-tbl-btn-actor-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.actor,
                                event.actor
                              )
                            }
                          >
                            <p className="email">{event.actor}</p>
                          </StyledButton>
                          <StyledButton
                            type="button"
                            data-testid={`at-evt-tbl-btn-actortype-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.actorType,
                                event.actorType
                              )
                            }
                          >
                            {event.actorType && (
                              <p className="font-weight-bold">{`(${event.actorType})`}</p>
                            )}
                          </StyledButton>
                        </td>
                        <td>
                          <StyledButton
                            isError={event.status.includes("Failed:")}
                            type="button"
                            data-testid={`at-evt-tbl-btn-subject-account-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.subjectAccount,
                                event.subject
                              )
                            }
                          >
                            <p className="email">{event.subject}</p>
                          </StyledButton>
                        </td>
                        <td>
                          <StyledButton
                            type="button"
                            isError={event.status.includes("Failed:")}
                            className={
                              user_sites?.length !== 0 ? "disabled" : ""
                            }
                            disabled={user_sites?.length !== 0}
                            data-testid={`at-evt-tbl-btn-subjectsite-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.subjectSite,
                                event.subjectSite
                              )
                            }
                          >
                            <p>{event.subjectSite}</p>
                          </StyledButton>
                        </td>
                        <td>
                          <StyledButton
                            type="button"
                            data-testid={`at-evt-tbl-btn-action-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.action,
                                event.action
                              )
                            }
                          >
                            <p>{formatActionField(event.action)}</p>
                          </StyledButton>
                        </td>
                        <td>{event.reason}</td>
                        <td
                          style={
                            event.status.includes("Succeeded")
                              ? {
                                  color: "green",
                                  fontWeight: "bold",
                                }
                              : event.status.includes("Failed:")
                              ? {
                                  color: "red",
                                  fontWeight: "bold",
                                }
                              : {}
                          }
                        >
                          <StyledButton
                            type="button"
                            className="colorized"
                            data-testid={`at-evt-tbl-btn-status-${index}`}
                            onClick={() =>
                              onSelectResource(
                                AUDIT_TRAIL_PARAMS_NAMES.status,
                                event.status
                              )
                            }
                          >
                            <p>{event.status}</p>
                          </StyledButton>
                        </td>
                        <td>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <StyledTooltip>{event.resource}</StyledTooltip>
                            }
                          >
                            <ResourceButton
                              type="button"
                              data-testid={`at-evt-tbl-btn-resource-${index}`}
                              onClick={() =>
                                onSelectResource(
                                  AUDIT_TRAIL_PARAMS_NAMES.resource,
                                  event.resource
                                )
                              }
                            >
                              <span
                                style={
                                  event.status.includes("Failed:")
                                    ? {
                                        color: "red",
                                      }
                                    : {}
                                }
                              >
                                {shrinkResource(event.resource, 6)}
                              </span>
                            </ResourceButton>
                          </OverlayTrigger>
                        </td>

                        {hasMoreDataValues ? (
                          <TdCloseFullDetails
                            stripped={evenRow}
                            colSpan={2}
                            open={event.open}
                          >
                            <StyledAuditTrailOutlineButton
                              className="noprint"
                              variant="outline-primary"
                              data-testid="reset-filters-button"
                              onClick={() => toggleMoreEventData(event)}
                              size="sm"
                            >
                              {event.open ? "Close details" : "Details"}
                            </StyledAuditTrailOutlineButton>
                            <PrintableText>See details below</PrintableText>
                          </TdCloseFullDetails>
                        ) : (
                          <>
                            <td>
                              <DiffView
                                hasMoreData={hasMoreDataOldValue}
                                oldData={event.oldValue}
                                newData={event.newValue}
                              >
                                <StyledValue
                                  $hasMoreData={hasMoreDataOldValue}
                                  diff={event.oldValue && event.newValue}
                                >
                                  {event.oldValue}
                                </StyledValue>
                              </DiffView>
                            </td>
                            <td>
                              <DiffView
                                hasMoreData={hasMoreDataNewValue}
                                oldData={event.oldValue || ""}
                                newData={event.newValue || ""}
                              >
                                <StyledValue
                                  $hasMoreData={hasMoreDataNewValue}
                                  diff={event.oldValue && event.newValue}
                                >
                                  {event.newValue}
                                </StyledValue>
                              </DiffView>
                            </td>
                          </>
                        )}

                        <td>
                          <StyledMeta>{event.meta}</StyledMeta>
                        </td>
                      </StrippedTr>

                      {event.open && hasMoreDataValues && (
                        <>
                          <StrippedTr
                            stripped={evenRow}
                            className={event.open ? "open" : ""}
                          >
                            <DiffViewContainer colSpan={10}>
                              <ReactDiffViewer
                                oldValue={event.oldValue || ""}
                                newValue={event.newValue || ""}
                                leftTitle="Original Value"
                                rightTitle="New Value"
                                splitView={true}
                                showDiffOnly={false}
                              />
                            </DiffViewContainer>
                          </StrippedTr>
                          <StrippedTr stripped={evenRow}>
                            <td colSpan={10}></td>
                          </StrippedTr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </StyledTable>
          </StyledAuditTrailEventsTable>
          <StyledPrintEndOfRecords
            className="d-flex justify-content-center"
            {...{ hasMoreData }}
          >
            End of retrieved records. There are more available on the server.
          </StyledPrintEndOfRecords>
          <div
            className="my-2 d-flex justify-content-around noprint"
            data-testid="load-more-container"
          >
            {hasMoreData && (
              <PrimaryButton
                data-testid="load-more-button"
                onClick={() => handleStatus(events[events.length - 1])}
                disabled={
                  hasMoreData === false ||
                  hasMoreData === "loading" ||
                  events.length < AUDIT_TRAIL_EVENTS_PAGE_SIZE
                }
              >
                Load More{" "}
                {hasMoreData === "loading" && (
                  <Spinner animation="border" size="sm" />
                )}
              </PrimaryButton>
            )}
          </div>
        </div>
      ) : hasMoreData === "loading" ? (
        <div className="d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      ) : hasMoreData === "noresults" ? (
        <div className="d-flex justify-content-center text-muted">
          <h4 className="pt-3">No results available for current filters.</h4>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

const StyledDiffView = styled.div`
  background: rgba(255, 255, 255, 0.5);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  -webkit-box-shadow: 3px 3px 10px 3px #bbb;
  -moz-box-shadow: 3px 3px 10px 3px #bbb;
  box-shadow: 3px 3px 10px 3px #bbb;
  z-index: 1050;
  max-width: 800px;
  letter-spacing: -1px;
`;

const DiffView = (props) => {
  const { oldData, newData, hasMoreData, children } = props;

  return oldData && newData && !hasMoreData ? (
    <OverlayTrigger
      placement="left-start"
      overlay={
        <StyledDiffView className="noprint">
          <ReactDiffViewer
            oldValue={oldData}
            newValue={newData}
            leftTitle="Original Value"
            rightTitle="New Value"
            splitView={true}
            showDiffOnly={false}
          />
        </StyledDiffView>
      }
    >
      {children}
    </OverlayTrigger>
  ) : (
    <>{children}</>
  );
};

export default withTheme(AuditTrailEventsTable);
